'use client';

import React, { useEffect, useRef, useState } from 'react';
import { createNoise3D } from 'simplex-noise';

import { cn } from '@/lib/utils';

export function WavyBackground({
  children,
  className,
  containerClassName,
  colors,
  waveWidth,
  backgroundFill,
  blur = 10,
  speed = 'fast',
  waveOpacity = 0.5,
  ...props
}: {
  children?: React.ReactNode;
  className?: string;
  containerClassName?: string;
  colors?: string[];
  waveWidth?: number;
  backgroundFill?: string;
  blur?: number;
  speed?: 'slow' | 'fast';
  waveOpacity?: number;
  [key: string]: unknown;
}) {
  const noise = createNoise3D();
  let w: number,
    h: number,
    nt: number,
    i: number,
    ctx: CanvasRenderingContext2D | null,
    canvas: HTMLCanvasElement | null;
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const getSpeed = () => {
    switch (speed) {
      case 'slow':
        return 0.001;
      case 'fast':
        return 0.002;
      default:
        return 0.001;
    }
  };

  const init = () => {
    canvas = canvasRef.current;
    if (canvas === null) return;

    ctx = canvas.getContext('2d');
    if (ctx === null) return;

    // Get actual container dimensions and round up to avoid gaps
    const container = canvas.parentElement;
    const rect = container?.getBoundingClientRect() ?? { width: window.innerWidth, height: window.innerHeight };

    w = ctx.canvas.width = Math.ceil(rect.width);
    h = ctx.canvas.height = Math.ceil(rect.height);
    ctx.filter = `blur(${blur}px)`;
    nt = 0;

    window.onresize = function () {
      if (ctx === null || canvas === null) return;
      const container = canvas.parentElement;
      const rect = container?.getBoundingClientRect() ?? { width: window.innerWidth, height: window.innerHeight };

      w = ctx.canvas.width = Math.ceil(rect.width);
      h = ctx.canvas.height = Math.ceil(rect.height);
      ctx.filter = `blur(${blur}px)`;
    };

    render();
  };

  // Use theme colors matching how.fm brand
  const waveColors = colors ?? [
    '#8b5cf6', // Primary purple
    '#009FB4', // Logo cyan
    '#FFA620', // Logo orange
    '#5F3EA7', // Logo purple
    '#06b6d4', // Secondary cyan
  ];

  const drawWave = (n: number) => {
    if (ctx === null) return;

    nt += getSpeed();
    for (i = 0; i < n; i++) {
      ctx.beginPath();
      ctx.lineWidth = waveWidth ?? 50;
      const color = waveColors[i % waveColors.length];
      if (color !== undefined) {
        ctx.strokeStyle = color;
      }
      // Draw vertical waves
      for (let y = 0; y < h; y += 5) {
        const x = noise(y / 800, 0.3 * i, nt) * 100;
        ctx.lineTo(x + w * 0.5, y); // adjust for width, currently at 50% of the container
      }
      ctx.stroke();
      ctx.closePath();
    }
  };

  let animationId: number;
  const render = () => {
    if (ctx === null) return;

    ctx.globalAlpha = 1.0;
    ctx.fillStyle = backgroundFill ?? 'hsl(0, 0%, 98.04%)';
    ctx.fillRect(0, 0, w, h);
    ctx.globalAlpha = waveOpacity;
    drawWave(5);
    animationId = requestAnimationFrame(render);
  };

  useEffect(() => {
    // Delay initialization to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      init();
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      cancelAnimationFrame(animationId);
    };
  }, []);

  const [isSafari, setIsSafari] = useState(false);
  useEffect(() => {
    // Safari support
    setIsSafari(
      typeof window !== 'undefined' &&
        navigator.userAgent.includes('Safari') &&
        navigator.userAgent.includes('Chrome') === false
    );
  }, []);

  return (
    <div
      className={cn(
        'h-screen flex flex-col items-center justify-center',
        containerClassName
      )}
      style={{ backgroundColor: backgroundFill ?? 'hsl(0, 0%, 98.04%)' }}
    >
      <canvas
        className="absolute inset-0 z-0"
        ref={canvasRef}
        id="canvas"
        style={{
          ...(isSafari === true ? { filter: `blur(${blur}px)` } : {}),
        }}
      />
      <div className={cn('relative z-10', className)} {...props}>
        {children}
      </div>
    </div>
  );
}